import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  IconButton,
  Collapse,
} from '@mui/material';
import {
  Refresh,
  TrendingUp,
  ShoppingCart,
  Assessment,
  BarChart as BarChartIcon,
  KeyboardArrowDown,
  KeyboardArrowUp,
  CheckCircle,
  Cancel,
} from '@mui/icons-material';
import { apiService } from '../../services/api';
import { formatLocalDate, formatInTimezone } from '../../utils/dateUtils';
import {
  LineChart,
  Line,
  BarChart as RechartsBarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import type { PieLabelRenderProps } from 'recharts';

interface DailyPurchaseAnalytics {
  date: string;
  totalPurchases: number;
  totalRevenue: number;
  totalCredits: number;
  verifiedPurchases: number;
  uniqueUsersCount: number;
  platforms: {
    google_play?: number;
    app_store?: number;
  };
  products: {
    [key: string]: {
      count: number;
      revenue: number;
      credits: number;
    };
  };
}

interface ProductAnalytics {
  productId: string;
  count: number;
  totalRevenue: number;
  totalCredits: number;
  verifiedCount: number;
  unverifiedCount: number;
  uniqueUsersCount: number;
  avgPrice: number;
}

interface UserPurchaseAnalytics {
  userId: string;
  email?: string;
  displayName?: string;
  firebaseUserId?: string;
  totalPurchases: number;
  totalRevenue: number;
  totalCredits: number;
  verifiedPurchases: number;
  firstPurchase: string;
  lastPurchase: string;
}

interface IndividualPurchase {
  _id: string;
  userId: string;
  productId: string;
  platform: string;
  price: number;
  currency: string;
  creditsAdded: number;
  verified: boolean;
  timestamp: string;
  transactionId?: string;
  orderId?: string;
  purchaseId?: string;
  userEmail?: string;
  userDisplayName?: string;
}

const PurchaseAnalytics: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Date filters
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });
  const [platformFilter, setPlatformFilter] = useState<string>('');
  const [productFilter, setProductFilter] = useState<string>('');
  const [timezone, setTimezone] = useState<string>('UTC');

  // Overview analytics
  const [overview, setOverview] = useState<any>(null);

  // Daily analytics
  const [dailyAnalytics, setDailyAnalytics] = useState<DailyPurchaseAnalytics[]>([]);
  const [dailySummary, setDailySummary] = useState<any>(null);

  // Product analytics
  const [productAnalytics, setProductAnalytics] = useState<ProductAnalytics[]>([]);

  // User analytics
  const [userAnalytics, setUserAnalytics] = useState<UserPurchaseAnalytics[]>([]);

  // Expandable rows state for daily analytics
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [datePurchases, setDatePurchases] = useState<{ [date: string]: IndividualPurchase[] }>({});
  const [loadingPurchases, setLoadingPurchases] = useState<{ [date: string]: boolean }>({});

  useEffect(() => {
    // Clear cached individual purchases so they re-fetch with the new filters/timezone
    setDatePurchases({});
    setExpandedRows(new Set());
    loadAnalytics();
  }, [tabValue, startDate, endDate, platformFilter, productFilter, timezone]);

  const loadAnalytics = async () => {
    setLoading(true);
    setError(null);

    try {
      if (tabValue === 0) {
        // Overview
        const response = await apiService.getPurchaseAnalyticsOverview(
          startDate,
          endDate,
          timezone
        );
        if (response.success && response.data) {
          setOverview(response.data.overview || null);
        } else {
          setError('Failed to load overview');
        }
      } else if (tabValue === 1) {
        // Daily analytics
        const response = await apiService.getDailyPurchaseAnalytics(
          startDate,
          endDate,
          platformFilter || undefined,
          productFilter || undefined,
          timezone
        );
        if (response.success && response.data) {
          setDailyAnalytics(response.data.analytics || []);
          setDailySummary(response.data.summary || null);
        } else {
          setError('Failed to load daily analytics');
        }
      } else if (tabValue === 2) {
        // Product analytics
        const response = await apiService.getProductPurchaseAnalytics(
          startDate,
          endDate,
          timezone
        );
        if (response.success && response.data) {
          setProductAnalytics(response.data.products || []);
        } else {
          setError('Failed to load product analytics');
        }
      } else if (tabValue === 3) {
        // User analytics
        const response = await apiService.getUserPurchaseAnalytics(
          startDate,
          endDate,
          undefined,
          timezone
        );
        if (response.success && response.data) {
          setUserAnalytics(response.data.users || []);
        } else {
          setError('Failed to load user analytics');
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const getProductLabel = (productId: string) => {
    const labels: { [key: string]: string } = {
      credits_100: 'Starter Spark',
      credits_1200: 'Creator Pack',
      credits_20000: 'Ultimate Studio',
    };
    return labels[productId] || productId;
  };

  const getPlatformLabel = (platform: string) => {
    const labels: { [key: string]: string } = {
      google_play: 'Google Play',
      app_store: 'App Store',
    };
    return labels[platform] || platform;
  };

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount || 0);
  };

  const handleRowExpand = async (date: string) => {
    const newExpandedRows = new Set(expandedRows);

    if (expandedRows.has(date)) {
      // Collapse the row
      newExpandedRows.delete(date);
      setExpandedRows(newExpandedRows);
    } else {
      // Expand the row and fetch data if not already loaded
      newExpandedRows.add(date);
      setExpandedRows(newExpandedRows);

      if (!datePurchases[date]) {
        setLoadingPurchases(prev => ({ ...prev, [date]: true }));
        try {
          const response = await apiService.getPurchasesByDate(
            date,
            platformFilter || undefined,
            productFilter || undefined,
            timezone
          );
          if (response.success && response.data) {
            setDatePurchases(prev => ({
              ...prev,
              [date]: response.data.purchases || []
            }));
          }
        } catch (err) {
          console.error('Failed to fetch purchases for date:', date, err);
        } finally {
          setLoadingPurchases(prev => ({ ...prev, [date]: false }));
        }
      }
    }
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h4">Purchase Analytics</Typography>
        <Button
          startIcon={<Refresh />}
          onClick={loadAnalytics}
          variant="outlined"
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={2}>
              <TextField
                label="Start Date"
                type="date"
                fullWidth
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                label="End Date"
                type="date"
                fullWidth
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <FormControl fullWidth>
                <InputLabel>Platform</InputLabel>
                <Select
                  value={platformFilter}
                  label="Platform"
                  onChange={(e) => setPlatformFilter(e.target.value)}
                >
                  <MenuItem value="">All Platforms</MenuItem>
                  <MenuItem value="google_play">Google Play</MenuItem>
                  <MenuItem value="app_store">App Store</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <InputLabel>Product</InputLabel>
                <Select
                  value={productFilter}
                  label="Product"
                  onChange={(e) => setProductFilter(e.target.value)}
                >
                  <MenuItem value="">All Products</MenuItem>
                  <MenuItem value="credits_100">Starter Spark</MenuItem>
                  <MenuItem value="credits_1200">Creator Pack</MenuItem>
                  <MenuItem value="credits_20000">Ultimate Studio</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <InputLabel>Timezone</InputLabel>
                <Select
                  value={timezone}
                  label="Timezone"
                  onChange={(e) => setTimezone(e.target.value)}
                >
                  <MenuItem value="UTC">UTC</MenuItem>
                  <MenuItem value="Asia/Kolkata">Asia/Kolkata (IST)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper>
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
          <Tab label="Overview" icon={<Assessment />} iconPosition="start" />
          <Tab label="Daily Analytics" icon={<BarChartIcon />} iconPosition="start" />
          <Tab label="Product Analytics" icon={<ShoppingCart />} iconPosition="start" />
          <Tab label="User Analytics" icon={<TrendingUp />} iconPosition="start" />
        </Tabs>

        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {tabValue === 0 && (
              <Box p={3}>
                {overview ? (
                  <>
                    {/* Key Metrics */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      <Grid item xs={12} sm={6} md={3}>
                        <Card>
                          <CardContent>
                            <Typography variant="subtitle2" color="text.secondary">
                              Total Purchases
                            </Typography>
                            <Typography variant="h4">{overview.totalPurchases || 0}</Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Card>
                          <CardContent>
                            <Typography variant="subtitle2" color="text.secondary">
                              Total Revenue
                            </Typography>
                            <Typography variant="h4" color="success.main">
                              {formatCurrency(overview.totalRevenue || 0)}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Card>
                          <CardContent>
                            <Typography variant="subtitle2" color="text.secondary">
                              Total Credits
                            </Typography>
                            <Typography variant="h4">{overview.totalCredits?.toLocaleString() || 0}</Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Card>
                          <CardContent>
                            <Typography variant="subtitle2" color="text.secondary">
                              Verification Rate
                            </Typography>
                            <Typography variant="h4" color={parseFloat(overview.verificationRate || 0) >= 90 ? 'success.main' : 'warning.main'}>
                              {overview.verificationRate || 0}%
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {overview.verifiedPurchases || 0} verified / {overview.unverifiedPurchases || 0} unverified
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>

                    {/* Platform Distribution */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      <Grid item xs={12} md={6}>
                        <Card>
                          <CardContent>
                            <Typography variant="h6" gutterBottom>Platform Distribution</Typography>
                            <ResponsiveContainer width="100%" height={300}>
                              <PieChart>
                                <Pie
                                  data={[
                                    { name: 'Google Play', value: overview.platformStats?.google_play || 0 },
                                    { name: 'App Store', value: overview.platformStats?.app_store || 0 },
                                  ]}
                                  cx="50%"
                                  cy="50%"
                                  labelLine={false}
                                  label={({ name, percent }: PieLabelRenderProps) => {
                                    const percentValue = typeof percent === 'number' ? percent : 0;
                                    return `${name} ${(percentValue * 100).toFixed(0)}%`;
                                  }}
                                  outerRadius={80}
                                  fill="#8884d8"
                                  dataKey="value"
                                >
                                  <Cell fill="#8884d8" />
                                  <Cell fill="#82ca9d" />
                                </Pie>
                                <Tooltip />
                              </PieChart>
                            </ResponsiveContainer>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Card>
                          <CardContent>
                            <Typography variant="h6" gutterBottom>Product Performance</Typography>
                            <ResponsiveContainer width="100%" height={300}>
                              <RechartsBarChart data={overview.productStats || []}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="productId" tickFormatter={getProductLabel} />
                                <YAxis />
                                <Tooltip />
                                <Legend />
                                <Bar dataKey="count" fill="#8884d8" name="Purchases" />
                                <Bar dataKey="totalRevenue" fill="#82ca9d" name="Revenue" />
                              </RechartsBarChart>
                            </ResponsiveContainer>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </>
                ) : (
                  <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                    No overview data available
                  </Typography>
                )}
              </Box>
            )}

            {tabValue === 1 && (
              <Box p={3}>
                {dailySummary && (
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={3}>
                      <Card>
                        <CardContent>
                          <Typography variant="subtitle2" color="text.secondary">
                            Total Purchases
                          </Typography>
                          <Typography variant="h4">{dailySummary.totalPurchases || 0}</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Card>
                        <CardContent>
                          <Typography variant="subtitle2" color="text.secondary">
                            Total Revenue
                          </Typography>
                          <Typography variant="h4" color="success.main">
                            {formatCurrency(dailySummary.totalRevenue || 0)}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Card>
                        <CardContent>
                          <Typography variant="subtitle2" color="text.secondary">
                            Total Credits
                          </Typography>
                          <Typography variant="h4">
                            {dailySummary.totalCredits?.toLocaleString() || 0}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Card>
                        <CardContent>
                          <Typography variant="subtitle2" color="text.secondary">
                            Verified Purchases
                          </Typography>
                          <Typography variant="h4" color="success.main">
                            {dailySummary.verifiedPurchases || 0}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                )}

                {dailyAnalytics.length > 0 && (
                  <ResponsiveContainer width="100%" height={400} style={{ marginBottom: 24 }}>
                    <LineChart data={dailyAnalytics}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" angle={-45} textAnchor="end" height={100} tickFormatter={(value) => formatLocalDate(value, 'MMM dd')} />
                      <YAxis yAxisId="left" />
                      <YAxis yAxisId="right" orientation="right" />
                      <Tooltip />
                      <Legend />
                      <Line yAxisId="left" type="monotone" dataKey="totalPurchases" stroke="#8884d8" name="Purchases" />
                      <Line yAxisId="right" type="monotone" dataKey="totalRevenue" stroke="#82ca9d" name="Revenue" />
                    </LineChart>
                  </ResponsiveContainer>
                )}

                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell width={50} />
                        <TableCell>Date</TableCell>
                        <TableCell align="right">Purchases</TableCell>
                        <TableCell align="right">Revenue</TableCell>
                        <TableCell align="right">Credits</TableCell>
                        <TableCell align="right">Verified</TableCell>
                        <TableCell align="right">Unique Users</TableCell>
                        <TableCell>Platforms</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dailyAnalytics.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                            <Typography color="text.secondary">
                              No analytics data found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        dailyAnalytics.map((item) => (
                          <React.Fragment key={item.date}>
                            <TableRow
                              hover
                              sx={{
                                cursor: 'pointer',
                                '& > *': { borderBottom: expandedRows.has(item.date) ? 'unset' : undefined }
                              }}
                              onClick={() => handleRowExpand(item.date)}
                            >
                              <TableCell>
                                <IconButton
                                  aria-label="expand row"
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRowExpand(item.date);
                                  }}
                                >
                                  {expandedRows.has(item.date) ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                </IconButton>
                              </TableCell>
                              <TableCell>
                                {formatLocalDate(item.date, 'MMM dd, yyyy')}
                              </TableCell>
                              <TableCell align="right">
                                <Typography fontWeight="bold">{item.totalPurchases}</Typography>
                              </TableCell>
                              <TableCell align="right">
                                {formatCurrency(item.totalRevenue)}
                              </TableCell>
                              <TableCell align="right">
                                {item.totalCredits?.toLocaleString()}
                              </TableCell>
                              <TableCell align="right">{item.verifiedPurchases}</TableCell>
                              <TableCell align="right">{item.uniqueUsersCount}</TableCell>
                              <TableCell>
                                {Object.entries(item.platforms || {}).map(([platform, count]) => (
                                  <Chip
                                    key={platform}
                                    label={`${getPlatformLabel(platform)}: ${count}`}
                                    size="small"
                                    sx={{ mr: 0.5 }}
                                  />
                                ))}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                                <Collapse in={expandedRows.has(item.date)} timeout="auto" unmountOnExit>
                                  <Box sx={{ margin: 2 }}>
                                    <Typography variant="h6" gutterBottom component="div">
                                      Individual Purchases - {formatLocalDate(item.date, 'MMM dd, yyyy')}
                                    </Typography>
                                    {loadingPurchases[item.date] ? (
                                      <Box display="flex" justifyContent="center" p={2}>
                                        <CircularProgress size={24} />
                                      </Box>
                                    ) : datePurchases[item.date]?.length === 0 ? (
                                      <Typography color="text.secondary" align="center" sx={{ py: 2 }}>
                                        No purchases found for this date
                                      </Typography>
                                    ) : (
                                      <Table size="small" aria-label="purchases">
                                        <TableHead>
                                          <TableRow>
                                            <TableCell>Time</TableCell>
                                            <TableCell>User</TableCell>
                                            <TableCell>Product</TableCell>
                                            <TableCell>Platform</TableCell>
                                            <TableCell align="right">Price</TableCell>
                                            <TableCell align="right">Credits</TableCell>
                                            <TableCell align="center">Verified</TableCell>
                                            <TableCell>Purchase ID</TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {(datePurchases[item.date] || []).map((purchase) => (
                                            <TableRow key={purchase._id} hover>
                                              <TableCell>
                                                {formatInTimezone(purchase.timestamp, timezone, 'HH:mm:ss')}
                                              </TableCell>
                                              <TableCell>
                                                <Box>
                                                  <Typography variant="body2">
                                                    {purchase.userDisplayName || 'Unknown'}
                                                  </Typography>
                                                  {purchase.userEmail && (
                                                    <Typography variant="caption" color="text.secondary">
                                                      {purchase.userEmail}
                                                    </Typography>
                                                  )}
                                                </Box>
                                              </TableCell>
                                              <TableCell>
                                                <Chip
                                                  label={getProductLabel(purchase.productId)}
                                                  size="small"
                                                  color="primary"
                                                  variant="outlined"
                                                />
                                              </TableCell>
                                              <TableCell>
                                                <Chip
                                                  label={getPlatformLabel(purchase.platform)}
                                                  size="small"
                                                  color={purchase.platform === 'app_store' ? 'info' : 'success'}
                                                  variant="outlined"
                                                />
                                              </TableCell>
                                              <TableCell align="right">
                                                {formatCurrency(purchase.price, purchase.currency || 'USD')}
                                              </TableCell>
                                              <TableCell align="right">
                                                {purchase.creditsAdded?.toLocaleString()}
                                              </TableCell>
                                              <TableCell align="center">
                                                {purchase.verified ? (
                                                  <CheckCircle color="success" fontSize="small" />
                                                ) : (
                                                  <Cancel color="error" fontSize="small" />
                                                )}
                                              </TableCell>
                                              <TableCell>
                                                <Typography
                                                  variant="caption"
                                                  sx={{
                                                    fontFamily: 'monospace',
                                                    fontSize: '0.75rem'
                                                  }}
                                                  title={purchase.purchaseId || purchase.transactionId || purchase.orderId || 'N/A'}
                                                >
                                                  {purchase.purchaseId || purchase.transactionId || purchase.orderId || 'N/A'}
                                                </Typography>
                                              </TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    )}
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {tabValue === 2 && (
              <Box p={3}>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Product</TableCell>
                        <TableCell align="right">Purchases</TableCell>
                        <TableCell align="right">Revenue</TableCell>
                        <TableCell align="right">Credits</TableCell>
                        <TableCell align="right">Verified</TableCell>
                        <TableCell align="right">Unverified</TableCell>
                        <TableCell align="right">Unique Users</TableCell>
                        <TableCell align="right">Avg Price</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {productAnalytics.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                            <Typography color="text.secondary">
                              No product analytics found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        productAnalytics.map((product) => (
                          <TableRow key={product.productId} hover>
                            <TableCell>
                              <Typography fontWeight="bold">
                                {getProductLabel(product.productId)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography fontWeight="bold">{product.count}</Typography>
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrency(product.totalRevenue)}
                            </TableCell>
                            <TableCell align="right">
                              {product.totalCredits?.toLocaleString()}
                            </TableCell>
                            <TableCell align="right" sx={{ color: 'success.main' }}>
                              {product.verifiedCount}
                            </TableCell>
                            <TableCell align="right" sx={{ color: 'error.main' }}>
                              {product.unverifiedCount}
                            </TableCell>
                            <TableCell align="right">{product.uniqueUsersCount}</TableCell>
                            <TableCell align="right">
                              {formatCurrency(product.avgPrice)}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {tabValue === 3 && (
              <Box p={3}>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>User</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell align="right">Purchases</TableCell>
                        <TableCell align="right">Revenue</TableCell>
                        <TableCell align="right">Credits</TableCell>
                        <TableCell align="right">Verified</TableCell>
                        <TableCell>First Purchase</TableCell>
                        <TableCell>Last Purchase</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {userAnalytics.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                            <Typography color="text.secondary">
                              No user analytics found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        userAnalytics.map((user) => (
                          <TableRow key={user.userId} hover>
                            <TableCell>
                              {user.displayName || 'Unknown'}
                            </TableCell>
                            <TableCell>{user.email || 'N/A'}</TableCell>
                            <TableCell align="right">
                              <Typography fontWeight="bold">{user.totalPurchases}</Typography>
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrency(user.totalRevenue)}
                            </TableCell>
                            <TableCell align="right">
                              {user.totalCredits?.toLocaleString()}
                            </TableCell>
                            <TableCell align="right" sx={{ color: 'success.main' }}>
                              {user.verifiedPurchases}
                            </TableCell>
                            <TableCell>
                              {formatInTimezone(user.firstPurchase, timezone, 'MMM dd, yyyy HH:mm')}
                            </TableCell>
                            <TableCell>
                              {formatInTimezone(user.lastPurchase, timezone, 'MMM dd, yyyy HH:mm')}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
};

export default PurchaseAnalytics;


